import React from 'react';
import {
  Cart,
  existingComponents,
  OrderHistory,
  ProductDetails,
  retailPreviewOrderDetails,
  retailPreviewProductDetail,
} from '../../TemplateComponentRegistry.jsx';
import {
  EducationAboutPageSection,
  EducationContactPageSection,
  EducationCourseDetailPageSection,
  EducationProductsPageSection,
  educationReaderAssets,
} from '../education/EducationTemplateSections.jsx';
import { GenericTemplateSection } from '../generic/GenericTemplateSections.jsx';

export const EducationWrapperSection = ({ section, theme }) => {
  const componentKey = section.content?.componentKey || section.bindings?.componentKey;
  const Component = existingComponents[componentKey];
  return Component ? <Component /> : <GenericTemplateSection section={section} theme={theme} />;
};

export const formatFallbackTitle = (value = '') => (
  String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

export class SectionRenderErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch() {}

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const section = this.props.section || {};
    const fallbackTitle = this.props.fallbackTitle
      || section.content?.title
      || section.name
      || formatFallbackTitle(section.content?.componentKey || section.bindings?.componentKey || section.sectionId || 'Template section');

    return (
      <GenericTemplateSection
        section={{
          ...section,
          content: {
            ...(section.content || {}),
            title: fallbackTitle,
            description: section.content?.description || 'This section is temporarily unavailable.'
          }
        }}
        theme={this.props.theme || {}}
        fallbackTitle={fallbackTitle}
      />
    );
  }
}

export const ExistingComponentSection = ({ section, theme, template }) => {
  const componentKey = section.content?.componentKey || section.bindings?.componentKey;
  const previewKey = section.content?.previewKey || section.bindings?.previewKey;
  const domainType = template?.domain?.type || template?.attributes?.sourceDomain || '';
  if (domainType === 'EDUCATION') {
    if (['educationAbout', 'educationAboutAcademy'].includes(previewKey) || ['About', 'AcademyAbout'].includes(componentKey)) {
      return <EducationAboutPageSection section={{ ...section, type: 'educationAbout' }} theme={theme} />;
    }
    if (['educationContact', 'sharedContact'].includes(previewKey) || ['Contact', 'KrumagoContact'].includes(componentKey)) {
      return <EducationContactPageSection section={{ ...section, type: 'educationContact' }} theme={theme} />;
    }
    if (['educationProducts'].includes(previewKey) || componentKey === 'Products') {
      return <EducationProductsPageSection section={{ ...section, type: 'educationProductsPage', style: { ...(section.style || {}), maxWidth: section.style?.maxWidth || '1536px' } }} theme={theme} />;
    }
    if (['educationCourseDetail'].includes(previewKey) || componentKey === 'CourseInformation') {
      return (
        <EducationCourseDetailPageSection
          section={{
            ...section,
            type: 'educationCourseDetail',
            style: { ...(section.style || {}), maxWidth: section.style?.maxWidth || '1536px' },
            content: {
              title: 'Course Detail',
              subtitle: 'Review curriculum, schedule, instructor information, and enrollment options.',
              imageUrl: educationReaderAssets.course,
              ...(section.content || {})
            }
          }}
          theme={theme}
        />
      );
    }
  }
  if (domainType === 'RETAIL') {
    if (['retailProductDetail'].includes(previewKey) || componentKey === 'ProductDetails') {
      const isPreviewRoute = typeof window !== 'undefined' && window.location.pathname === '/template-preview';
      return (
        <ProductDetails
          templateTheme={theme}
          template={template}
          previewProduct={isPreviewRoute ? retailPreviewProductDetail : null}
        />
      );
    }
    if (['retailSummary'].includes(previewKey) || componentKey === 'OrderHistory') {
      const isPreviewRoute = typeof window !== 'undefined' && window.location.pathname === '/template-preview';
      return (
        <OrderHistory
          templateTheme={theme}
          template={template}
          previewOrderDetails={isPreviewRoute ? retailPreviewOrderDetails : null}
          previewTotalAmount={218}
        />
      );
    }
    if (['retailCart'].includes(previewKey) || ['RetailCart', 'Cart'].includes(componentKey)) {
      return <Cart templateTheme={theme} template={template} />;
    }
  }
  const Component = existingComponents[componentKey];
  return typeof Component === 'function' || typeof Component === 'string'
    ? <Component templateTheme={theme} template={template} templateDomainType={domainType} />
    : <GenericTemplateSection section={section} theme={theme} fallbackTitle={formatFallbackTitle(componentKey || previewKey || section.name)} />;
};
